import React, { FC, useCallback, useState } from 'react'
import { Column } from 'react-table'
import { Box, Button, Checkbox, Container, Divider, FormControlLabel, FormGroup, FormLabel, Grid, makeStyles, TextField, Typography } from '@material-ui/core'
import { TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import MUITable from '@material-ui/core/Table'
import Table from '../../components/Table'
import FormDialogWithDelete from "../../components/dialogs/FormDialogWithDelete";
import moment from "moment"
import "moment/locale/da"
import * as AlarmsAPI from '../../api/alarms/index'
import * as AlarmsModels from '../../api/alarms/models'
import * as ShortModels from '../../api/short/models'
import APIRequest from '../../components/helpers/APIRequest'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

interface Props {
    locationMap: Map<number, string>
    locationData: ShortModels.Location[]
}

const Groups: FC<Props> = ({locationMap, locationData}) => {
    const classes = useStyles()

    const [groups, setGroups] = useState<AlarmsModels.Group[]>([])
    const [isPendingGetGroups, setPendingGetGroups] = useState(false)
    const [runGetGroups, setRunGetGroups] = useState(true)

    // Dialog and related API control
    const [runRequestStoreGroup, setRunRequestStoreGroup] = useState(false)
    const [isPendingRequestStoreGroup, setPendingRunRequestStoreGroup] = useState(false)
    const [runRequestDeleteGroup, setRunRequestDeleteGroup] = useState(false)
    const [isPendingRequestDeleteGroup, setPendingRunRequestDeleteGroup] = useState(false)
    const [editGroupOpen, setEditGroupOpen] = useState(false)

    // Group definition for the edit dialog
    const [groupDialogTitle, setGroupDialogTitle] = useState("")
    const [groupDialogDeleteButtonText, setGroupDialogDeleteButtonText] = useState("")
    const [groupId, setGroupId] = useState<number>(0)
    const [groupName, setGroupName] = useState("")
    const [groupLocations, setGroupLocations] = useState<number[]>([])
    const [groupSendAlarm, setGroupSendAlarm] = useState(false)
    const [groupSendAlarmDeactivated, setGroupSendAlarmDeactivated] = useState(false)
    const [groupSendWarning, setGroupSendWarning] = useState(false)
    const [groupSendWarningDeactivated, setGroupSendWarningDeactivated] = useState(false)
    const [groupUnitCPUCard, setGroupUnitCPUCard] = useState(false)
    const [groupUnitDisplay, setGroupUnitDisplay] = useState(false)
    const [groupUnitPrinter, setGroupUnitPrinter] = useState(false)
    const [groupUnitPaperStatus, setGroupUnitPaperStatus] = useState(false)
    const [groupUnitChipCardReader, setGroupUnitChipCardReader] = useState(false)
    const [groupUnitCoinUnit, setGroupUnitCoinUnit] = useState(false)
    const [groupUnitDoor, setGroupUnitDoor] = useState(false)
    const [groupUnitNetwork, setGroupUnitNetwork] = useState(false)

    const boolToString = (set: boolean) => {
        if (set) {
            return "Ja"
        }
        return "Nej"
    }

    const renderLocationsCell = useCallback((locationIDList: number[]) => {
        if (!locationIDList) {
            return ""
        }
        let locationNames: string[] = []
        locationIDList.forEach(id => {
            var locationName = locationMap.get(id)
            if (locationName) {
                if (locationName.length > 0) {
                    locationNames.push(locationName)
                }
            }
        })
        return locationNames.join(", ")
    }, [locationMap])

    const renderUnitsCell = (group: AlarmsModels.Group) => {
        let u: string[] = []
        if (group.cpuCard) { u.push("CPU-kort")}
        if (group.display) { u.push("display")}
        if (group.printer) { u.push("printer")}
        if (group.paperStatus) { u.push("papirstatus")}
        if (group.chipCardReader) { u.push("chipkortlæser")}
        if (group.coinUnit) { u.push("møntenhed")}
        if (group.door) { u.push("frontlåge")}
        if (group.network) { u.push("netværksenhed")}
        var s = u.join(", ")
        return s.charAt(0).toUpperCase() + s.slice(1)
    }

    const columns: Column<AlarmsModels.Group>[] = React.useMemo(() => [
        { Header: 'Navn', id: "0", accessor: 'name' },
        { Header: 'Pladser', id: "1", accessor: d => renderLocationsCell(d.locations), style: { 'white-space': 'unset' /*wrap line*/ } },
        { Header: 'Enheder', id: "2", accessor: d => renderUnitsCell(d) },
        { Header: 'Send alarm opstået', id: "3", accessor: d => boolToString(d.sendAlarm) },
        { Header: 'Send alarm ophørt', id: "4", accessor: d => boolToString(d.sendAlarmDeactivated) },
        { Header: 'Send advarsel opstået', id: "5", accessor: d => boolToString(d.sendWarning) },
        { Header: 'Send advarsel ophørt', id: "6", accessor: d => boolToString(d.sendWarningDeactivated) }
    ], [renderLocationsCell])

    const handleClickNewGroup = () => {
        handleRowClick(new AlarmsModels.Group())
    }

    const handleRowClick = (element: AlarmsModels.Group) => {
        if (element.id > 0) {
            setGroupDialogTitle("Ændring af gruppering")
            setGroupDialogDeleteButtonText("Slet gruppering")
        } else {
            setGroupDialogTitle("Ny gruppering")
            setGroupDialogDeleteButtonText("")
        }
        setGroupId(element.id)
        setGroupName(element.name)
        setGroupLocations(element.locations)
        setGroupSendAlarm(element.sendAlarm)
        setGroupSendAlarmDeactivated(element.sendAlarmDeactivated)
        setGroupSendWarning(element.sendWarning)
        setGroupSendWarningDeactivated(element.sendWarningDeactivated)
        setGroupUnitCPUCard(element.cpuCard)
        setGroupUnitDisplay(element.display)
        setGroupUnitPrinter(element.printer)
        setGroupUnitPaperStatus(element.paperStatus)
        setGroupUnitChipCardReader(element.chipCardReader)
        setGroupUnitCoinUnit(element.coinUnit)
        setGroupUnitDoor(element.door)
        setGroupUnitNetwork(element.network)
        setEditGroupOpen(true)
    }

    const RequestGetGroups = () => {
        return (<APIRequest<AlarmsModels.Group[]>
            run={runGetGroups} setRun={setRunGetGroups}
            pending={isPendingGetGroups} setIsPending={setPendingGetGroups}
            req={() => AlarmsAPI.getGroups()}
            setData={setGroups}
        />)
    }

    const RequestStoreGroup = () => {
        let group: AlarmsModels.Group = {id: groupId, name: groupName.trim(), locations: groupLocations, sendAlarm: groupSendAlarm, sendAlarmDeactivated: groupSendAlarmDeactivated, sendWarning: groupSendWarning, sendWarningDeactivated: groupSendWarningDeactivated,
            cpuCard: groupUnitCPUCard, display: groupUnitDisplay, printer: groupUnitPrinter, paperStatus: groupUnitPaperStatus, chipCardReader: groupUnitChipCardReader,
            coinUnit: groupUnitCoinUnit, door: groupUnitDoor, network: groupUnitNetwork}
        return (<APIRequest<AlarmsModels.Group[]>
            run={runRequestStoreGroup} setRun={setRunRequestStoreGroup}
            pending={isPendingRequestStoreGroup} setIsPending={setPendingRunRequestStoreGroup}
            req={() => AlarmsAPI.storeGroup(group)}
            setData={setGroups}
        />)
    }

    const RequestDeleteGroup = () => {
        return (<APIRequest<AlarmsModels.Group[]>
            run={runRequestDeleteGroup} setRun={setRunRequestDeleteGroup}
            pending={isPendingRequestDeleteGroup} setIsPending={setPendingRunRequestDeleteGroup}
            req={() => AlarmsAPI.deleteGroup(groupId)}
            setData={setGroups}
        />)
    }

    // Function needed for the group edit dialog. Returns true if the location is selected
    const isLocationSelected = (id: number) => groupLocations.indexOf(id) !== -1

    // Function needed for the group edit dialog. Selects all or no locations
    const handleSelectAllLocationsClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelects = locationData.map((n) => n.id)
            setGroupLocations(newSelects)
        } else {
            setGroupLocations([])
        }
    }

    // Function needed for the group edit dialog. Adds or removes a selected location
    const handleSelectLocationClick = (event: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = groupLocations.indexOf(id)
        let newSelected: number[] = []

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(groupLocations, id)
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(groupLocations.slice(1))
        } else if (selectedIndex === groupLocations.length - 1) {
            newSelected = newSelected.concat(groupLocations.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(groupLocations.slice(0, selectedIndex), groupLocations.slice(selectedIndex + 1),)
        }

        setGroupLocations(newSelected)
    }

    return (
        <Container maxWidth={false}>
            <RequestGetGroups />
            <RequestStoreGroup />
            <RequestDeleteGroup />
            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Grupperinger</Typography>
                <div className={classes.paper}>
                    <Button variant="contained" color="primary" className={classes.buts} onClick={handleClickNewGroup}>Ny gruppering</Button>
                </div>
                <div className={classes.grid}>
                    <Table<AlarmsModels.Group> inputFullWidth columns={columns} data={groups} initialState={{pageSize: 150}} clickEvent={handleRowClick} update={null} pending={false} text={""}/>
                </div>
            </div>

            <FormDialogWithDelete title={groupDialogTitle} open={editGroupOpen} deleteText={groupDialogDeleteButtonText} saveText="Gem gruppering"
                    onClose={() => setEditGroupOpen(false)} onCloseSubmit={() => setRunRequestStoreGroup(true)} onCloseDelete={() => setRunRequestDeleteGroup(true)}>
                <Grid container spacing={2}>
                    <Grid container item xs={6} direction="column" >
                        <TableContainer>
                            <MUITable aria-labelledby="tableTitle" size='medium' aria-label="enhanced table" className="classes.paper">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox indeterminate={groupLocations.length > 0 && groupLocations.length < locationData.length} checked={groupLocations.length > 0 && groupLocations.length === locationData.length} onChange={handleSelectAllLocationsClick} inputProps={{ 'aria-label': 'Vælg alle pladser' }} />
                                        </TableCell>
                                        <TableCell align='left'>Pladser</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {locationData.map((row, index) => (
                                        <TableRow key={row.id} onClick={(event) => handleSelectLocationClick(event, row.id)}>
                                            <TableCell padding="checkbox">
                                                <Checkbox checked={isLocationSelected(row.id)} inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${index}` }} />
                                            </TableCell>
                                            <TableCell component="th" scope="row">
                                                {row.name}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </MUITable>
                        </TableContainer>
                    </Grid>
                    <Grid container item xs={6} direction="column" >
                        <Box mt={2} mb={2}>
                            <TextField  style ={{minWidth: '250px'}} autoComplete="name" name="name" variant="outlined" fullWidth id="name" label="Navn" value={groupName} autoFocus onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupName(e.currentTarget.value);}} />
                        </Box>
                        <FormLabel component="legend">Alarmhændelser</FormLabel>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupSendAlarm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupSendAlarm(e.currentTarget.checked);}} />}
                                label="Send alarm opstået" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupSendAlarmDeactivated} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupSendAlarmDeactivated(e.currentTarget.checked);}} />}
                                label="Send alarm ophørt" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupSendWarning} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupSendWarning(e.currentTarget.checked);}} />}
                                label="Send advarsel opstået" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupSendWarningDeactivated} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupSendWarningDeactivated(e.currentTarget.checked);}} />}
                                label="Send advarsel ophørt" />
                        </FormGroup>
                        <Divider />
                        <Box mt={1}>
                            <FormLabel component="legend">Enheder</FormLabel>
                        </Box>
                        <FormGroup>
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitCPUCard} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitCPUCard(e.currentTarget.checked);}} />}
                                label="CPU-kort" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitDisplay} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitDisplay(e.currentTarget.checked);}} />}
                                label="Display" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitPrinter} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitPrinter(e.currentTarget.checked);}} />}
                                label="Printer" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitPaperStatus} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitPaperStatus(e.currentTarget.checked);}} />}
                                label="Papirstatus" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitChipCardReader} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitChipCardReader(e.currentTarget.checked);}} />}
                                label="Chipkortlæser" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitCoinUnit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitCoinUnit(e.currentTarget.checked);}} />}
                                label="Møntenhed" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitDoor} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitDoor(e.currentTarget.checked);}} />}
                                label="Frontlåge" />
                            <FormControlLabel
                                control={<Checkbox name="active" id="active" checked={groupUnitNetwork} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setGroupUnitNetwork(e.currentTarget.checked);}} />}
                                label="Netværksenhed" />
                        </FormGroup>
                    </Grid>
                </Grid>
            </FormDialogWithDelete>
        </Container>
    )
}

export default Groups
